import React, { useState } from 'react';
import { DotRow } from './progress/DotRow';
import { Lock } from 'lucide-react';
import { Panel } from './ui/panel';

interface Row {
  label: string;
  color: string;
  glow: string;
  maxCount: number;
  count: number;
}

interface AdvancedModeProgressPanelProps {
  rows: Row[];
  isDotsComplete: boolean;
  onSkillChallengeClick?: () => void;
  embedded?: boolean;
}

const AdvancedModeProgressPanel: React.FC<AdvancedModeProgressPanelProps> = ({
  rows,
  isDotsComplete,
  onSkillChallengeClick,
  embedded = false,
}) => {
  type CheckpointVars = React.CSSProperties & {
    '--checkpoint-size': string;
    '--line-thickness': string;
    '--complete-color': string;
    '--incomplete-color': string;
    '--checkpoint-track': string;
  };

  const panelRef = React.useRef<HTMLDivElement>(null);
  const lockRef = React.useRef<HTMLDivElement>(null);
  const dotsCheckpointRef = React.useRef<HTMLDivElement>(null);
  const buttonRowRef = React.useRef<HTMLDivElement>(null);

  const [dotsAnimating, setDotsAnimating] = useState<boolean>(false);
  const prevDotsComplete = React.useRef<boolean>(isDotsComplete);

  const [linePositions, setLinePositions] = React.useState({
    dotsCheckpointY: 0,
    buttonY: 0,
  });

  const measure = React.useCallback(() => {
    if (!panelRef.current || !dotsCheckpointRef.current || !buttonRowRef.current) return;
    const panel = panelRef.current.getBoundingClientRect();
    const dots = dotsCheckpointRef.current.getBoundingClientRect();
    const button = buttonRowRef.current.getBoundingClientRect();
    setLinePositions({
      dotsCheckpointY: dots.top + dots.height / 2 - panel.top,
      buttonY: button.top + button.height / 2 - panel.top,
    });
  }, []);

  React.useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    panelRef.current && ro.observe(panelRef.current);
    dotsCheckpointRef.current && ro.observe(dotsCheckpointRef.current);
    buttonRowRef.current && ro.observe(buttonRowRef.current);
    window.addEventListener('resize', measure);
    const id = requestAnimationFrame(measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      cancelAnimationFrame(id);
    };
  }, [measure, rows, isDotsComplete]);

  React.useEffect(() => {
    if (!prevDotsComplete.current && isDotsComplete) {
      setDotsAnimating(true);
      const t = setTimeout(() => setDotsAnimating(false), 600);
      return () => clearTimeout(t);
    }
    prevDotsComplete.current = isDotsComplete;
  }, [isDotsComplete]);

  const content = (
    <>
      <h3
        className="tw-uppercase tw-tracking-wider"
        style={{
          fontFamily: 'var(--font-poppins)',
          fontSize: '14px',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--muted-foreground)',
          textAlign: 'center',
          margin: 'var(--spacing-4) var(--spacing-4) var(--spacing-2)',
          padding: 0,
        }}
      >
        Practice Unlocked!
      </h3>
      <p
        style={{
          fontFamily: 'var(--font-atkinson)',
          fontSize: 'var(--text-sm)',
          color: 'var(--muted-foreground)',
          textAlign: 'center',
          margin: '0 var(--spacing-4) var(--spacing-4)',
          lineHeight: 1.4,
        }}
      >
        Get 5 right in a row and you&apos;ll be ready for the skill challenge.
      </p>

      <div
        ref={panelRef}
        style={
          {
            flex: 1,
            display: 'grid',
            gridTemplateColumns:
              '[checkpoint-left] var(--checkpoint-track) [content-start] 1fr [content-end] var(--checkpoint-track) [checkpoint-right]',
            gridTemplateRows: '[dots] auto [button] auto',
            position: 'relative',
            '--checkpoint-size': '1.5rem',
            '--line-thickness': '2px',
            '--complete-color': '#10b981',
            '--incomplete-color': '#9ca3af',
            '--checkpoint-track': '3rem',
          } as CheckpointVars
        }
      >
        <div
          ref={dotsCheckpointRef}
          className="tw-grid tw-place-items-center"
          style={{
            gridColumn: '1',
            gridRow: 'dots',
            zIndex: 10,
            alignSelf: 'center',
            justifySelf: 'stretch',
            width: '100%',
            position: 'relative',
          }}
        >
          {dotsAnimating && (
            <div
              className="tw-absolute tw-rounded-full tw-animate-ping tw-bg-green-500"
              style={{ width: 'var(--checkpoint-size)', height: 'var(--checkpoint-size)', opacity: 0.6 }}
            />
          )}
          <div
            className={`tw-rounded-full tw-border-2 tw-transition-all tw-duration-500 tw-flex tw-items-center tw-justify-center ${
              isDotsComplete
                ? 'tw-bg-green-500 tw-border-green-500'
                : 'tw-bg-white dark:tw-bg-gray-800 tw-border-gray-400'
            }`}
            style={{
              width: 'var(--checkpoint-size)',
              height: 'var(--checkpoint-size)',
              animation: dotsAnimating ? 'checkboxPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'none',
            }}
          >
            {isDotsComplete && (
              <svg
                className="tw-w-4 tw-h-4 tw-text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
                style={{ animation: dotsAnimating ? 'checkmarkDraw 0.5s ease-out 0.1s both' : 'none' }}
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>

        <div
          className="tw-absolute tw-inset-0 tw-pointer-events-none tw-rounded-2xl tw-overflow-hidden"
          style={{ zIndex: 1 }}
        >
          <div
            className="tw-absolute tw-transition-all"
            style={{
              left: 'calc(var(--checkpoint-track) / 2)',
              top: `${linePositions.dotsCheckpointY}px`,
              height: `${Math.max(0, linePositions.buttonY - linePositions.dotsCheckpointY)}px`,
              width: 0,
              borderRight: `var(--line-thickness) ${isDotsComplete ? 'solid' : 'dashed'} ${
                isDotsComplete ? 'var(--complete-color)' : 'var(--incomplete-color)'
              }`,
            }}
          />
          <div
            className="tw-absolute"
            style={{
              left: 'calc(var(--checkpoint-track) / 2)',
              width: 'calc(50% - var(--checkpoint-track) / 2)',
              top: `${linePositions.buttonY}px`,
              height: 0,
              borderTop: `var(--line-thickness) ${isDotsComplete ? 'solid' : 'dashed'} ${
                isDotsComplete ? 'var(--complete-color)' : 'var(--incomplete-color)'
              }`,
            }}
          />
        </div>

        <div
          className="tw-border-b"
          style={{
            gridColumn: 'content-start / content-end',
            gridRow: 'dots',
            position: 'relative',
            zIndex: 2,
            paddingTop: 'var(--spacing-4)',
            paddingBottom: 'var(--spacing-4)',
            paddingLeft: 'var(--spacing-6)',
            paddingRight: 'var(--spacing-6)',
            borderColor: 'var(--border)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {rows.map((row, idx) => (
            <div key={idx} style={{ marginBottom: idx < rows.length - 1 ? 'var(--spacing-6)' : '0' }}>
              <div
                style={{
                  fontFamily: 'var(--font-poppins)',
                  fontSize: '12px',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--muted-foreground)',
                  marginBottom: 'var(--spacing-1)',
                  paddingLeft: 'var(--spacing-4)',
                  lineHeight: '1',
                }}
              >
                {row.label}
              </div>
              <DotRow
                filledCount={row.count}
                primaryColor={row.color}
                glowColor={row.glow}
                rowIndex={idx}
                maxCount={row.maxCount}
              />
            </div>
          ))}
        </div>

        <div
          ref={buttonRowRef}
          style={{
            gridColumn: 'content-start / content-end',
            gridRow: 'button',
            position: 'relative',
            zIndex: 2,
            paddingTop: 'var(--spacing-4)',
            paddingBottom: 'var(--spacing-4)',
            paddingLeft: 'var(--spacing-6)',
            paddingRight: 'var(--spacing-6)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {!isDotsComplete ? (
            <div
              ref={lockRef}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--spacing-4)' }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                <Lock style={{ width: '28px', height: '28px', color: 'var(--muted-foreground)' }} />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-4)' }}>
              <button
                onClick={() => onSkillChallengeClick?.()}
                className="skill-challenge-button"
                style={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                  color: 'var(--primary-foreground)',
                  borderRadius: 'var(--radius-button)',
                  border: 'none',
                  cursor: 'pointer',
                  paddingTop: 'var(--spacing-4)',
                  paddingBottom: 'var(--spacing-4)',
                  paddingLeft: 'var(--spacing-6)',
                  paddingRight: 'var(--spacing-6)',
                  fontFamily: 'var(--font-atkinson)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-bold)',
                  boxShadow: '0 0 30px var(--primary), 0 0 50px var(--accent)',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s',
                  animation: 'skillChallengePulse 1.5s ease-in-out infinite',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  minWidth: 'fit-content',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                  e.currentTarget.style.boxShadow = '0 0 40px var(--primary), 0 0 80px var(--accent)';
                  e.currentTarget.style.animation = 'none';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 0 30px var(--primary), 0 0 50px var(--accent)';
                  e.currentTarget.style.animation = 'skillChallengePulse 1.5s ease-in-out infinite';
                }}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>Take the Skill Challenge</span>
                <div
                  style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
                    animation: 'shimmer 2.5s infinite',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {embedded ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{content}</div>
      ) : (
        <Panel
          className="panel-scrollbar"
          style={{
            height: '100%',
            width: '400px',
            minWidth: '400px',
            maxWidth: '400px',
            flexShrink: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {content}
        </Panel>
      )}
      <style>{`
        @keyframes checkboxPop { 0% { transform: scale(0.5); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes checkmarkDraw {
          0% { transform: scale(0) rotate(-45deg); opacity: 0; }
          50% { transform: scale(1.1) rotate(5deg); }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes skillChallengePulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 30px var(--primary), 0 0 50px var(--accent);
          }
          50% {
            transform: scale(1.12);
            box-shadow: 0 0 40px var(--primary), 0 0 70px var(--accent);
          }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        .skill-challenge-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 50%, rgba(255, 255, 255, 0.3) 100%);
          opacity: 0;
          transition: opacity 0.3s;
          border-radius: var(--radius-button);
        }
        .skill-challenge-button:hover::before {
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default AdvancedModeProgressPanel;
